/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */

import {isLoggedIn} from '@github-ui/client-env'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useConfirm} from '@primer/react'
import {useCallback} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {usePinnedComment} from '../../hooks/use-pinned-comment'
import {commitDeleteIssueCommentMutation} from '../../mutations/delete-issue-comment-mutation'
import {minimizeCommentMutation} from '../../mutations/minimize-comment-mutation'
import {unminimizeCommentMutation} from '../../mutations/unminimize-comment-mutation'
import type {CommentAuthorType} from '../ActivityHeader'
import {CommentHeader} from '../CommentHeader'
import type {IssueCommentHeader$key} from './__generated__/IssueCommentHeader.graphql'

export type IssueCommentHeaderProps = {
  anchorBaseUrl?: string
  comment: IssueCommentHeader$key
  commentAuthorLogin: string
  editComment: () => void
  onReplySelect: (quotedText?: string) => void
  isMinimized?: boolean
  onMinimize?: (value: boolean) => void
  navigate: (url: string) => void
  avatarUrl: string
  commentRef?: React.RefObject<HTMLDivElement | null>
  relayConnectionIds?: string[]
  commentSubjectAuthorLogin?: string
  hideActions?: boolean
  commentAuthorType?: CommentAuthorType
  hasDisabledActions?: boolean
  showCopilotMenuItem?: boolean
}

export function IssueCommentHeader({
  anchorBaseUrl,
  comment,
  commentAuthorLogin,
  editComment,
  onReplySelect,
  isMinimized = false,
  onMinimize: onMinimized,
  navigate,
  avatarUrl,
  commentRef,
  relayConnectionIds,
  commentSubjectAuthorLogin,
  hideActions = false,
  commentAuthorType,
  hasDisabledActions = false,
  showCopilotMenuItem = false,
}: IssueCommentHeaderProps) {
  const commentData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueCommentHeader on IssueComment {
        id
        databaseId
        url
        createdAt
        body
        authorAssociation
        viewerCanUpdate
        viewerCanDelete
        viewerCanMinimize
        viewerCanUnminimize
        viewerCanReport
        viewerCanReportToMaintainer
        viewerCanBlockFromOrg
        viewerCanUnblockFromOrg
        viewerCanPin
        viewerCanUnpin
        viaApp {
          name
          url
        }
        isHidden: isMinimized
        minimizedReason
        pendingMinimizeReason
        pendingBlock
        pendingUnblock
        showSpammyBadge
        createdViaEmail
        viewerDidAuthor
        authorToRepoOwnerSponsorship {
          createdAt
          isActive
        }
        author {
          id
          login
          ... on User {
            name
          }
          ... on EnterpriseUserAccount {
            name
          }
          ... on Mannequin {
            name
          }
        }
        repository {
          id
          name
          owner {
            id
            login
            url
          }
          isPrivate
        }
        issue {
          number
        }
        ...MarkdownEditHistoryViewer_comment
        ...MarkdownLastEditedBy
        pinnedBy {
          login
        }
      }
    `,
    comment,
  )

  const environment = useRelayEnvironment()
  const confirm = useConfirm()
  const {addToast} = useToastContext()
  const {pinComment, unpinComment} = usePinnedComment()

  const hideComment = useCallback(
    async (reason: 'SPAM' | 'ABUSE' | 'OFF_TOPIC' | 'OUTDATED' | 'DUPLICATE' | 'RESOLVED') => {
      minimizeCommentMutation({
        environment,
        input: {id: commentData.id, reason},
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: 'Failed to hide comment',
          })
        },
      })
    },
    [addToast, environment, commentData.id],
  )

  const unhideComment = useCallback(async () => {
    unminimizeCommentMutation({
      environment,
      input: {id: commentData.id},
      onError: () => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: 'Failed to unhide comment',
        })
      },
    })
  }, [addToast, environment, commentData.id])

  const deleteComment = useCallback(async () => {
    const performDelete = await confirm({
      title: LABELS.confirmations.deleteCommentTitle,
      content: LABELS.confirmations.deleteCommentContent,
      confirmButtonContent: LABELS.confirmations.deleteCommentConfirmButtonContent,
      confirmButtonType: 'danger',
    })

    if (!performDelete) return

    const connections = relayConnectionIds ?? []

    commitDeleteIssueCommentMutation({
      environment,
      connectionIds: connections,
      input: {id: commentData.id},
      onError: () => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotDeleteComment,
        })
      },
    })
  }, [confirm, commentData.id, relayConnectionIds, environment, addToast])

  const url = anchorBaseUrl ? `${anchorBaseUrl}#issuecomment-${commentData.databaseId}` : commentData.url

  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags
  const pinningEnabled = useFeatureFlag('issue_comment_pinning') && isLoggedIn()
  const viewerCanPin = pinningEnabled && commentData.viewerCanPin
  const viewerCanUnpin = pinningEnabled && commentData.viewerCanUnpin

  return (
    <CommentHeader
      comment={{
        ...commentData,
        referenceText: `#${commentData.issue.number}`,
        url,
        viewerCanSeeMinimizeButton: true,
        viewerCanSeeUnminimizeButton: true,
        viewerCanUnminimize: commentData.viewerCanUnminimize ?? false,
        viaApp: commentData.viaApp || undefined,
        viewerCanPin,
        viewerCanUnpin,
      }}
      editComment={editComment}
      onReplySelect={onReplySelect}
      isMinimized={isMinimized}
      onMinimize={onMinimized}
      commentAuthorLogin={commentAuthorLogin}
      commentAuthorProfileName={commentData.author?.name}
      navigate={navigate}
      avatarUrl={avatarUrl}
      hideComment={hideComment}
      unhideComment={unhideComment}
      deleteComment={deleteComment}
      commentRef={commentRef}
      viewerDidAuthor={commentData.viewerDidAuthor}
      commentSubjectAuthorLogin={commentSubjectAuthorLogin}
      commentSubjectType="issue"
      hideActions={hideActions}
      isDisabled={hasDisabledActions}
      commentAuthorType={commentAuthorType}
      id={`issuecomment-${commentData.databaseId}`}
      showCopilotMenuItem={showCopilotMenuItem}
      pinComment={pinComment}
      unpinComment={unpinComment}
      pinnedByLogin={commentData?.pinnedBy?.login}
    />
  )
}
